/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  AmrUtilizationStatus,
  CardStatus,
  DiversionType,
  OperationType,
} from 'src/graphql/generated/graphql.generated';
import {
  OperationSummaryRecord,
  OperationSummaryProps,
} from 'src/store/generated/operation-summary-record.generated';
import {
  RequestSummaryRecord,
  RequestSummaryProps,
} from 'src/store/generated/request-summary-record.generated';
import {
  RequestPatientSummaryRecord,
  RequestPatientSummaryProps,
} from 'src/store/generated/request-patient-summary-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface CardSummaryProps {
  id: string;
  status: CardStatus;
  cardNumber: string;
  dateContactCompleted: Date | null;
  operation: OperationSummaryProps | null;
  request: RequestSummaryProps | null;
  patients: List<RequestPatientSummaryProps> | null;
  apiWebhookSubscriptionId: string | null;
  webOrderAcknowledgedTime: Date | null;
  operationType: OperationType;
  diversionType: DiversionType | null;
  gmrUtilizationStatus: AmrUtilizationStatus | null;
  gmrCADNumber: string | null;
  vehicleId: string | null;
  vehicleSummaryId: string | null;
  communicationCenterId: string | null;
  unitId: string | null;
  unitSummaryId: string | null;
  regionId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
}

export class CardSummaryRecord extends Record<CardSummaryProps>({
  id: null as any,
  status: null as any,
  cardNumber: null as any,
  dateContactCompleted: null as any,
  operation: null as any,
  request: null as any,
  patients: null as any,
  apiWebhookSubscriptionId: null as any,
  webOrderAcknowledgedTime: null as any,
  operationType: null as any,
  diversionType: null as any,
  gmrUtilizationStatus: null as any,
  gmrCADNumber: null as any,
  vehicleId: null as any,
  vehicleSummaryId: null as any,
  communicationCenterId: null as any,
  unitId: null as any,
  unitSummaryId: null as any,
  regionId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
}) {
  public static fromJS(json: {
    id: string;
    status: CardStatus;
    cardNumber: string;
    dateContactCompleted: string | number | Date | null;
    operation: any;
    request: any;
    patients: any;
    apiWebhookSubscriptionId: string | null;
    webOrderAcknowledgedTime: string | number | Date | null;
    operationType: OperationType;
    diversionType: DiversionType | null;
    gmrUtilizationStatus: AmrUtilizationStatus | null;
    gmrCADNumber: string | null;
    vehicleId: string | null;
    vehicleSummaryId: string | null;
    communicationCenterId: string | null;
    unitId: string | null;
    unitSummaryId: string | null;
    regionId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
  }): CardSummaryRecord {
    if (json == null) {
      throw new Error('CardSummaryRecord.fromJS: parameter cannot be null');
    }
    return new CardSummaryRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'CardSummaryJson.id',
      ),
      status: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'status', CardStatus.AwaitingAssignment),
        'CardSummaryJson.status',
      ),
      cardNumber: getFromOrThrow(json, (j) => j.cardNumber, 'CardSummaryJson.cardNumber'),
      dateContactCompleted:
        json.dateContactCompleted != null ? new Date(json.dateContactCompleted) : null,
      operation: json.operation != null ? new OperationSummaryRecord(json.operation) : null,
      request: json.request != null ? new RequestSummaryRecord(json.request) : null,
      patients:
        json.patients != null
          ? List<RequestPatientSummaryRecord>(
              json.patients.map((o: any) => new RequestPatientSummaryRecord(o)),
            )
          : null,
      apiWebhookSubscriptionId: json.apiWebhookSubscriptionId,
      webOrderAcknowledgedTime:
        json.webOrderAcknowledgedTime != null ? new Date(json.webOrderAcknowledgedTime) : null,
      operationType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'operationType', OperationType.SceneCall),
        'CardSummaryJson.operationType',
      ),
      diversionType: json.diversionType,
      gmrUtilizationStatus: json.gmrUtilizationStatus,
      gmrCADNumber: json.gmrCADNumber,
      vehicleId: json.vehicleId,
      vehicleSummaryId: json.vehicleSummaryId,
      communicationCenterId: json.communicationCenterId,
      unitId: json.unitId,
      unitSummaryId: json.unitSummaryId,
      regionId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'regionId', '00000000-0000-0000-0000-000000000000'),
        'CardSummaryJson.regionId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'CardSummaryJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    status: CardStatus;
    cardNumber: string;
    dateContactCompleted: string | number | Date | null;
    operation: any;
    request: any;
    patients: any;
    apiWebhookSubscriptionId: string | null;
    webOrderAcknowledgedTime: string | number | Date | null;
    operationType: OperationType;
    diversionType: DiversionType | null;
    gmrUtilizationStatus: AmrUtilizationStatus | null;
    gmrCADNumber: string | null;
    vehicleId: string | null;
    vehicleSummaryId: string | null;
    communicationCenterId: string | null;
    unitId: string | null;
    unitSummaryId: string | null;
    regionId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
  }): [CardSummaryRecord, Error[]] {
    if (json == null) {
      throw new Error('CardSummaryRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new CardSummaryRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'CardSummaryJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      status: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'status', CardStatus.AwaitingAssignment),
            'CardSummaryJson.status',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cardNumber: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.cardNumber, 'CardSummaryJson.cardNumber'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateContactCompleted: tryOrDefault(
        () => (json.dateContactCompleted != null ? new Date(json.dateContactCompleted) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      operation: tryOrDefault(
        () => (json.operation != null ? new OperationSummaryRecord(json.operation) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      request: tryOrDefault(
        () => (json.request != null ? new RequestSummaryRecord(json.request) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      patients: tryOrDefault(
        () =>
          json.patients != null
            ? List<RequestPatientSummaryRecord>(
                json.patients.map((o: any) => new RequestPatientSummaryRecord(o)),
              )
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      apiWebhookSubscriptionId: tryOrDefault(
        () => json.apiWebhookSubscriptionId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      webOrderAcknowledgedTime: tryOrDefault(
        () =>
          json.webOrderAcknowledgedTime != null ? new Date(json.webOrderAcknowledgedTime) : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      operationType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'operationType', OperationType.SceneCall),
            'CardSummaryJson.operationType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      diversionType: tryOrDefault(
        () => json.diversionType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      gmrUtilizationStatus: tryOrDefault(
        () => json.gmrUtilizationStatus,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      gmrCADNumber: tryOrDefault(
        () => json.gmrCADNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleId: tryOrDefault(
        () => json.vehicleId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleSummaryId: tryOrDefault(
        () => json.vehicleSummaryId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      communicationCenterId: tryOrDefault(
        () => json.communicationCenterId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitId: tryOrDefault(
        () => json.unitId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitSummaryId: tryOrDefault(
        () => json.unitSummaryId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      regionId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'regionId', '00000000-0000-0000-0000-000000000000'),
            'CardSummaryJson.regionId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'CardSummaryJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
    });

    return [record, errors];
  }

  constructor(props: CardSummaryProps) {
    super(props);
    if (props == null) {
      throw new Error('CardSummaryRecord constructor parameter cannot be null');
    }
  }
}

export abstract class CardSummaryViewModelBase implements CardSummaryProps {
  protected readonly _props: CardSummaryProps;
  private _json?: CardSummaryProps;

  get id(): string {
    return this._props.id;
  }
  get status(): CardStatus {
    return this._props.status;
  }
  get cardNumber(): string {
    return this._props.cardNumber;
  }
  get dateContactCompleted(): Date | null {
    return this._props.dateContactCompleted;
  }
  get operation(): OperationSummaryProps | null {
    return this._props.operation;
  }
  get request(): RequestSummaryProps | null {
    return this._props.request;
  }
  get patients(): List<RequestPatientSummaryProps> | null {
    return this._props.patients;
  }
  get apiWebhookSubscriptionId(): string | null {
    return this._props.apiWebhookSubscriptionId;
  }
  get webOrderAcknowledgedTime(): Date | null {
    return this._props.webOrderAcknowledgedTime;
  }
  get operationType(): OperationType {
    return this._props.operationType;
  }
  get diversionType(): DiversionType | null {
    return this._props.diversionType;
  }
  get gmrUtilizationStatus(): AmrUtilizationStatus | null {
    return this._props.gmrUtilizationStatus;
  }
  get gmrCADNumber(): string | null {
    return this._props.gmrCADNumber;
  }
  get vehicleId(): string | null {
    return this._props.vehicleId;
  }
  get vehicleSummaryId(): string | null {
    return this._props.vehicleSummaryId;
  }
  get communicationCenterId(): string | null {
    return this._props.communicationCenterId;
  }
  get unitId(): string | null {
    return this._props.unitId;
  }
  get unitSummaryId(): string | null {
    return this._props.unitSummaryId;
  }
  get regionId(): string {
    return this._props.regionId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }

  public get isAwaitingAssignment() {
    return this._props.status === CardStatus.AwaitingAssignment;
  }
  public get isAwaitingLaunch() {
    return this._props.status === CardStatus.AwaitingLaunch;
  }
  public get isClosed() {
    return this._props.status === CardStatus.Closed;
  }
  public get isCompleted() {
    return this._props.status === CardStatus.Completed;
  }
  public get isUnderway() {
    return this._props.status === CardStatus.Underway;
  }
  public get isGroundRun() {
    return this._props.operationType === OperationType.GroundRun;
  }
  public get isMaintenance() {
    return this._props.operationType === OperationType.Maintenance;
  }
  public get isNonPatientShuttle() {
    return this._props.operationType === OperationType.NonPatientShuttle;
  }
  public get isPatientShuttle() {
    return this._props.operationType === OperationType.PatientShuttle;
  }
  public get isPublicRelations() {
    return this._props.operationType === OperationType.PublicRelations;
  }
  public get isPublicUtility() {
    return this._props.operationType === OperationType.PublicUtility;
  }
  public get isReposition() {
    return this._props.operationType === OperationType.Reposition;
  }
  public get isResidence() {
    return this._props.operationType === OperationType.Residence;
  }
  public get isSceneCall() {
    return this._props.operationType === OperationType.SceneCall;
  }
  public get isSearchAndAssist() {
    return this._props.operationType === OperationType.SearchAndAssist;
  }
  public get isTraining() {
    return this._props.operationType === OperationType.Training;
  }
  public get isTransfer() {
    return this._props.operationType === OperationType.Transfer;
  }
  public get isTransferCenter() {
    return this._props.operationType === OperationType.TransferCenter;
  }
  public get isDiversion() {
    return this._props.diversionType === DiversionType.Diversion;
  }
  public get isFollowOn() {
    return this._props.diversionType === DiversionType.FollowOn;
  }
  public get isNoResourcesAvailable() {
    return this._props.gmrUtilizationStatus === AmrUtilizationStatus.NoResourcesAvailable;
  }
  public get isNoServiceAvailable() {
    return this._props.gmrUtilizationStatus === AmrUtilizationStatus.NoServiceAvailable;
  }
  public get isNotConsidered() {
    return this._props.gmrUtilizationStatus === AmrUtilizationStatus.NotConsidered;
  }
  public get isUnsatisfactoryEta() {
    return this._props.gmrUtilizationStatus === AmrUtilizationStatus.UnsatisfactoryEta;
  }
  public get isUnspecified() {
    return this._props.gmrUtilizationStatus === AmrUtilizationStatus.Unspecified;
  }
  public get isUtilized() {
    return this._props.gmrUtilizationStatus === AmrUtilizationStatus.Utilized;
  }

  public isAnyStatus(...cardStatus: CardStatus[]) {
    return cardStatus.includes(this._props.status);
  }
  public isAnyOperationType(...operationType: OperationType[]) {
    return operationType.includes(this._props.operationType);
  }
  public isAnyDiversionType(...diversionType: DiversionType[]) {
    return this._props.diversionType != null && diversionType.includes(this._props.diversionType);
  }
  public isAnyGmrUtilizationStatus(...amrUtilizationStatus: AmrUtilizationStatus[]) {
    return (
      this._props.gmrUtilizationStatus != null &&
      amrUtilizationStatus.includes(this._props.gmrUtilizationStatus)
    );
  }

  constructor(props: CardSummaryProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        status: this._props.status,
        cardNumber: this._props.cardNumber,
        dateContactCompleted: this._props.dateContactCompleted,
        operation: this._props.operation,
        request: this._props.request,
        patients: this._props.patients,
        apiWebhookSubscriptionId: this._props.apiWebhookSubscriptionId,
        webOrderAcknowledgedTime: this._props.webOrderAcknowledgedTime,
        operationType: this._props.operationType,
        diversionType: this._props.diversionType,
        gmrUtilizationStatus: this._props.gmrUtilizationStatus,
        gmrCADNumber: this._props.gmrCADNumber,
        vehicleId: this._props.vehicleId,
        vehicleSummaryId: this._props.vehicleSummaryId,
        communicationCenterId: this._props.communicationCenterId,
        unitId: this._props.unitId,
        unitSummaryId: this._props.unitSummaryId,
        regionId: this._props.regionId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllCardSummaryEntities = (state: AppState) =>
  state.entityStores.cardSummary.entities;

export const selectCardSummaryById = (id: string) => (state: AppState) =>
  state.entityStores.cardSummary.entities.get(id);

export const selectCardSummaryMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.cardSummary.meta.get(id);

export const selectCardSummaryAndMetadataById = (id: string) =>
  createSelector(
    selectCardSummaryById(id),
    selectCardSummaryMetadataById(id),
    (cardSummary, meta) => ({ cardSummary, meta }),
  );

export function selectCardSummaryEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllCardSummaryEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, CardSummaryRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectCardSummaryEntitiesByStatus(status: CardStatus) {
  return createSelector(selectAllCardSummaryEntities, (entities) =>
    entities.filter((e) => e.status === status).valueSeq(),
  );
}

export function selectCardSummaryEntitiesByCardNumber(cardNumber: string) {
  return createSelector(selectAllCardSummaryEntities, (entities) =>
    entities.filter((e) => e.cardNumber === cardNumber).valueSeq(),
  );
}

export function selectCardSummaryEntitiesByDateContactCompleted(dateContactCompleted: Date) {
  return createSelector(selectAllCardSummaryEntities, (entities) =>
    entities.filter((e) => e.dateContactCompleted === dateContactCompleted).valueSeq(),
  );
}

export function selectCardSummaryEntitiesByApiWebhookSubscriptionId(
  apiWebhookSubscriptionId: string,
) {
  return createSelector(selectAllCardSummaryEntities, (entities) =>
    entities.filter((e) => e.apiWebhookSubscriptionId === apiWebhookSubscriptionId).valueSeq(),
  );
}

export function selectCardSummaryEntitiesByWebOrderAcknowledgedTime(
  webOrderAcknowledgedTime: Date,
) {
  return createSelector(selectAllCardSummaryEntities, (entities) =>
    entities.filter((e) => e.webOrderAcknowledgedTime === webOrderAcknowledgedTime).valueSeq(),
  );
}

export function selectCardSummaryEntitiesByOperationType(operationType: OperationType) {
  return createSelector(selectAllCardSummaryEntities, (entities) =>
    entities.filter((e) => e.operationType === operationType).valueSeq(),
  );
}

export function selectCardSummaryEntitiesByDiversionType(diversionType: DiversionType) {
  return createSelector(selectAllCardSummaryEntities, (entities) =>
    entities.filter((e) => e.diversionType === diversionType).valueSeq(),
  );
}

export function selectCardSummaryEntitiesByGmrUtilizationStatus(
  gmrUtilizationStatus: AmrUtilizationStatus,
) {
  return createSelector(selectAllCardSummaryEntities, (entities) =>
    entities.filter((e) => e.gmrUtilizationStatus === gmrUtilizationStatus).valueSeq(),
  );
}

export function selectCardSummaryEntitiesByGmrCADNumber(gmrCADNumber: string) {
  return createSelector(selectAllCardSummaryEntities, (entities) =>
    entities.filter((e) => e.gmrCADNumber === gmrCADNumber).valueSeq(),
  );
}

export function selectCardSummaryEntitiesByVehicleId(vehicleId: string) {
  return createSelector(selectAllCardSummaryEntities, (entities) =>
    entities.filter((e) => e.vehicleId === vehicleId).valueSeq(),
  );
}

export function selectCardSummaryEntitiesByVehicleSummaryId(vehicleSummaryId: string) {
  return createSelector(selectAllCardSummaryEntities, (entities) =>
    entities.filter((e) => e.vehicleSummaryId === vehicleSummaryId).valueSeq(),
  );
}

export function selectCardSummaryEntitiesByCommunicationCenterId(communicationCenterId: string) {
  return createSelector(selectAllCardSummaryEntities, (entities) =>
    entities.filter((e) => e.communicationCenterId === communicationCenterId).valueSeq(),
  );
}

export function selectCardSummaryEntitiesByUnitId(unitId: string) {
  return createSelector(selectAllCardSummaryEntities, (entities) =>
    entities.filter((e) => e.unitId === unitId).valueSeq(),
  );
}

export function selectCardSummaryEntitiesByUnitSummaryId(unitSummaryId: string) {
  return createSelector(selectAllCardSummaryEntities, (entities) =>
    entities.filter((e) => e.unitSummaryId === unitSummaryId).valueSeq(),
  );
}

export function selectCardSummaryEntitiesByRegionId(regionId: string) {
  return createSelector(selectAllCardSummaryEntities, (entities) =>
    entities.filter((e) => e.regionId === regionId).valueSeq(),
  );
}

export function selectCardSummaryEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllCardSummaryEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
